/**
 * Client-side Xero Chart of Accounts helpers.
 * Filter rules mirror server/src/utils/xeroAccountValidation.js.
 */

const SALES_TYPES = new Set(['SALES', 'REVENUE', 'OTHERINCOME', 'DIRECTCOSTS']);
const EXPENSE_TYPES = new Set(['EXPENSE', 'DIRECTCOSTS', 'OVERHEADS']);

export function isValidSalesAccount(account) {
  if (!account) return false;
  return account.class === 'REVENUE' || SALES_TYPES.has(account.type);
}

export function isValidExpenseAccount(account) {
  if (!account) return false;
  return EXPENSE_TYPES.has(account.type);
}

export function filterSalesAccounts(accounts) {
  return (accounts || []).filter(isValidSalesAccount);
}

export function filterExpenseAccounts(accounts) {
  return (accounts || []).filter(isValidExpenseAccount);
}

/** Label format: "Sales (200)" */
export function formatAccountOptionLabel(account) {
  if (!account) return '';
  const name = account.name || 'Account';
  const code = account.code != null ? String(account.code) : '';
  return code ? `${name} (${code})` : name;
}

/**
 * Load cached Xero chart accounts; if empty, refresh from Xero and cache.
 * @returns {Promise<{ accounts: Array, fromCache: boolean }>}
 */
export async function loadXeroChartAccounts({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const cacheResponse = await fetch('/api/users/xero-chart-accounts');
    const cacheData = await cacheResponse.json();
    if (cacheResponse.ok && cacheData.success && Array.isArray(cacheData.accounts) && cacheData.accounts.length > 0) {
      return { accounts: cacheData.accounts, fromCache: true };
    }
  }

  const response = await fetch('/api/xero/chart-accounts');
  const data = await response.json();
  if (!response.ok || !data.success || !Array.isArray(data.accounts)) {
    throw new Error(data.error || 'Failed to fetch Xero chart of accounts');
  }

  await fetch('/api/users/xero-chart-accounts', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accounts: data.accounts })
  });

  return { accounts: data.accounts, fromCache: false };
}
